import React, { useState, useMemo, useEffect } from 'react';
import '../../../../assets/css/admin/dashboard/InvitationsTemplate.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';

// ── CONFIG ───────────────────────────────────────────────────────────────────
const ITM_API      = 'http://127.0.0.1:8000/api/admin/invitation-templates';
const getToken     = () => localStorage.getItem('token');

const ITM_EVENT_TYPES  = ['Wedding', 'Mehndi', 'Barat', 'Walima', 'Other'];
const ITM_THEMES       = ['Classic', 'Floral', 'Minimal', 'Modern', 'Royal'];
const ITM_ORIENTATIONS = ['Portrait', 'Landscape'];
const ITM_FONTS        = ['Playfair Display', 'Cormorant Garamond', 'Great Vibes', 'Dancing Script', 'Cinzel', 'Libre Baskerville'];
const ITM_PER_PAGE     = 8;
const ITM_HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const ITM_EMPTY_FORM = {
  name: '', eventType: 'Wedding', theme: 'Classic', orientation: 'Portrait',
  fontStyle: 'Playfair Display', fontColor: '#92400e',
  headerText: 'With Great Joy We Announce',
  bodyText: 'You are cordially invited...\nDate:\nVenue:\nTime:',
  footerText: 'RSVP:', bgImage: null, status: 'Active',
};

// ── HELPERS ──────────────────────────────────────────────────────────────────
const itmEventEmoji      = (type)  => ({ Wedding: '💍', Mehndi: '🌿', Barat: '🎊', Walima: '🍽️', Other: '✨' }[type]  || '✨');
const itmThemeEmoji      = (theme) => ({ Classic: '🌟', Floral: '🌸', Minimal: '🕊️', Modern: '🖤', Royal: '👑' }[theme] || '✉️');
const itmEventBadgeClass = (type)  => `itm-event-badge itm-event-badge${{ Wedding: '--wedding', Mehndi: '--mehndi', Barat: '--barat', Walima: '--walima', Other: '--other' }[type] || '--other'}`;
const itmThumbClass      = (theme) => `itm-template-thumb itm-template-thumb--${(theme || 'default').toLowerCase()}`;

// DB field names → form field names (for mapping API response)
const itmMapApiToForm = (item) => ({
  id:          item.id,
  name:        item.name        || '',
  eventType:   item.event_type  || 'Wedding',
  theme:       item.theme       || 'Classic',
  orientation: item.orientation || 'Portrait',
  fontStyle:   item.font_style  || 'Playfair Display',
  fontColor:   item.font_color  || '#92400e',
  headerText:  item.header_text || '',
  bodyText:    item.body_text   || '',
  footerText:  item.footer_text || '',
  bgImage:     item.bg_image    || null,
  status:      item.status      || 'Active',
  createdAt:   item.created_at
    ? (() => {
        const d = new Date(item.created_at);
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
      })()
    : '',
});

// ── TOAST ────────────────────────────────────────────────────────────────────
const ItmToast = ({ msg, type }) => (
  <div className={`itm-toast itm-toast--${type}`}>
    <span>{type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}</span>
    {msg}
  </div>
);

// ── INVITATION CARD PREVIEW ──────────────────────────────────────────────────
const ItmInvCard = ({ data }) => {
  const isDark    = data.theme === 'Modern' || data.theme === 'Royal';
  const textColor = isDark ? '#ffffff' : (data.fontColor || '#1c1917');
  const dimColor  = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(28,25,23,0.55)';
  return (
    <div
      className={[
        'itm-invitation-card',
        `itm-invitation-card--${(data.theme || 'classic').toLowerCase()}`,
        data.orientation === 'Landscape' ? 'itm-invitation-card--landscape' : '',
      ].join(' ')}
      style={{ fontFamily: `'${data.fontStyle || 'Playfair Display'}', serif` }}
    >
      <p className="itm-inv-bismillah" style={{ color: dimColor }}>Bismillah-ir-Rahman-ir-Raheem</p>
      <span className="itm-inv-ornament">〰 ✦ 〰</span>
      <h2 className="itm-inv-header" style={{ color: textColor }}>{data.headerText || 'Header Text'}</h2>
      <div className="itm-inv-divider" style={{ background: textColor }} />
      <p className="itm-inv-body" style={{ color: textColor, whiteSpace: 'pre-line' }}>{data.bodyText || 'Body text...'}</p>
      {data.footerText && (
        <>
          <div className="itm-inv-divider" style={{ background: textColor }} />
          <p className="itm-inv-footer" style={{ color: dimColor }}>{data.footerText}</p>
        </>
      )}
    </div>
  );
};

// ── ICON BUTTON STYLES ───────────────────────────────────────────────────────
const iconBtn = {
  width: '32px', height: '32px', padding: '0',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: '8px', border: 'none', cursor: 'pointer',
  fontSize: '13px', color: '#ffffff',
  transition: 'transform 0.18s ease, opacity 0.18s ease',
  flexShrink: 0,
};
const viewBtnStyle   = { ...iconBtn, background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', boxShadow: '0 3px 8px rgba(59,130,246,0.38)' };
const editBtnStyle   = { ...iconBtn, background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', boxShadow: '0 3px 8px rgba(245,158,11,0.38)' };
const deleteBtnStyle = { ...iconBtn, background: 'linear-gradient(135deg, #ef4444, #f87171)', boxShadow: '0 3px 8px rgba(239,68,68,0.38)' };
const hoverUp   = (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.88'; };
const hoverDown = (e) => { e.currentTarget.style.transform = 'translateY(0)';    e.currentTarget.style.opacity = '1'; };

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
const InvitationsTemplate = () => {

  const [itmScreen,    setItmScreen]    = useState('list');
  const [itmData,      setItmData]      = useState([]);
  const [itmForm,      setItmForm]      = useState(ITM_EMPTY_FORM);
  const [itmEditId,    setItmEditId]    = useState(null);
  const [itmViewItem,  setItmViewItem]  = useState(null);
  const [itmSearch,    setItmSearch]    = useState('');
  const [itmFltEvent,  setItmFltEvent]  = useState('All');
  const [itmFltStatus, setItmFltStatus] = useState('All');
  const [itmPage,      setItmPage]      = useState(1);
  const [itmDeleteId,  setItmDeleteId]  = useState(null);
  const [itmShowPrev,  setItmShowPrev]  = useState(false);
  const [itmPrevData,  setItmPrevData]  = useState(null);
  const [itmToast,     setItmToast]     = useState(null);
  const [itmLoading,   setItmLoading]   = useState(false);
  const [itmSaving,    setItmSaving]    = useState(false);

  // ── FETCH ALL ────────────────────────────────────────
  const itmFetchAll = async () => {
    setItmLoading(true);
    try {
      const res  = await fetch(ITM_API, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        setItmData(data.data.map(itmMapApiToForm));
      } else {
        itmShowToast(data.message || 'Failed to load templates.', 'error');
      }
    } catch (err) {
      itmShowToast('Server error. Could not load templates.', 'error');
    } finally {
      setItmLoading(false);
    }
  };

  useEffect(() => {
    itmFetchAll();
  }, []);

  // ── STATS ────────────────────────────────────────────
  const itmStats = useMemo(() => ({
    total:      itmData.length,
    active:     itmData.filter(t => t.status === 'Active').length,
    inactive:   itmData.filter(t => t.status === 'Inactive').length,
    eventTypes: [...new Set(itmData.map(t => t.eventType))].length,
  }), [itmData]);

  // ── FILTER + PAGINATE ─────────────────────────────────
  const itmFiltered = useMemo(() => itmData.filter(t => {
    const ms = t.name.toLowerCase().includes(itmSearch.toLowerCase());
    const me = itmFltEvent  === 'All' || t.eventType === itmFltEvent;
    const mk = itmFltStatus === 'All' || t.status    === itmFltStatus;
    return ms && me && mk;
  }), [itmData, itmSearch, itmFltEvent, itmFltStatus]);

  const itmTotalPages = Math.max(1, Math.ceil(itmFiltered.length / ITM_PER_PAGE));
  const itmPaginated  = itmFiltered.slice((itmPage - 1) * ITM_PER_PAGE, itmPage * ITM_PER_PAGE);

  // ── TOAST ─────────────────────────────────────────────
  const itmShowToast = (msg, type = 'success') => {
    setItmToast({ msg, type });
    setTimeout(() => setItmToast(null), 2800);
  };

  // ── NAVIGATION ────────────────────────────────────────
  const itmOpenCreate = () => {
    setItmEditId(null);
    setItmForm(ITM_EMPTY_FORM);
    setItmScreen('create');
    window.scrollTo(0, 0);
  };

  const itmOpenEdit = (item) => {
    setItmEditId(item.id);
    setItmForm({ ...item });
    setItmScreen('edit');
    window.scrollTo(0, 0);
  };

  const itmGoBack = () => {
    setItmScreen('list');
    setItmEditId(null);
    setItmForm(ITM_EMPTY_FORM);
    window.scrollTo(0, 0);
  };

  // ── VALIDATION (required fields + font color format) ──
  const itmValidateForm = () => {
    if (!itmForm.name.trim()) return 'Template name is required!';
    if (!itmForm.headerText.trim()) return 'Header text is required!';
    if (!ITM_HEX_COLOR_REGEX.test(itmForm.fontColor.trim())) return 'Font color must be a valid hex code (e.g. #92400e)';
    return null;
  };

  // ── SAVE (Create / Update) ────────────────────────────
  const itmSave = async () => {
    const validationError = itmValidateForm();
    if (validationError) {
      itmShowToast(validationError, 'error');
      return;
    }

    setItmSaving(true);

    const formData = new FormData();
    formData.append('name',        itmForm.name);
    formData.append('event_type',  itmForm.eventType);
    formData.append('theme',       itmForm.theme);
    formData.append('orientation', itmForm.orientation);
    formData.append('font_style',  itmForm.fontStyle);
    formData.append('font_color',  itmForm.fontColor);
    formData.append('header_text', itmForm.headerText);
    formData.append('body_text',   itmForm.bodyText   || '');
    formData.append('footer_text', itmForm.footerText || '');
    formData.append('status',      itmForm.status);
    if (itmForm.bgImage && typeof itmForm.bgImage !== 'string') {
      formData.append('bg_image', itmForm.bgImage);
    }

    try {
      const url = itmEditId !== null
        ? `${ITM_API}/${itmEditId}`
        : ITM_API;

      const res  = await fetch(url, {
        method:  'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body:    formData,
      });
      const data = await res.json();

      if (data.success) {
        itmShowToast(data.message || (itmEditId ? 'Template updated!' : 'Template created!'));
        await itmFetchAll();
        itmGoBack();
        setItmPage(1);
      } else {
        // Show validation errors if any
        if (data.errors) {
          const firstErr = Object.values(data.errors)[0];
          itmShowToast(Array.isArray(firstErr) ? firstErr[0] : firstErr, 'error');
        } else {
          itmShowToast(data.message || 'Something went wrong.', 'error');
        }
      }
    } catch (err) {
      itmShowToast('Server error. Please try again.', 'error');
    } finally {
      setItmSaving(false);
    }
  };

  // ── TOGGLE STATUS ─────────────────────────────────────
  const itmToggleStatus = async (id) => {
    try {
      const res  = await fetch(`${ITM_API}/${id}/toggle`, {
        method:  'PATCH',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        await itmFetchAll();
      } else {
        itmShowToast('Status toggle failed.', 'error');
      }
    } catch (err) {
      itmShowToast('Server error.', 'error');
    }
  };

  // ── DELETE ────────────────────────────────────────────
  const itmConfirmDelete = async () => {
    try {
      const res  = await fetch(`${ITM_API}/${itmDeleteId}`, {
        method:  'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) {
        itmShowToast('Template deleted.', 'error');
        setItmDeleteId(null);
        await itmFetchAll();
        if (itmScreen !== 'list') setItmScreen('list');
      } else {
        itmShowToast(data.message || 'Delete failed.', 'error');
      }
    } catch (err) {
      itmShowToast('Server error. Delete failed.', 'error');
    }
  };

  const itmReset        = () => { setItmSearch(''); setItmFltEvent('All'); setItmFltStatus('All'); setItmPage(1); };
  const itmDeleteTarget = itmData.find(t => t.id === itmDeleteId);

  // ── RENDER ─────────────────────────────────────────────
  return (
    <AdminLayout>
      <div className="itm-wrapper">

        {/* ════════ LIST ════════ */}
        {itmScreen === 'list' && (
          <div className="itm-list-page">

            <div className="itm-page-header">
              <div className="itm-page-header-left">
                <div className="itm-page-icon">✉️</div>
                <div>
                  <h1 className="itm-page-title">Invitation Templates</h1>
                  <p className="itm-page-subtitle">Design and manage wedding invitation templates</p>
                </div>
              </div>
              <button className="itm-btn-create" onClick={itmOpenCreate}>＋ Create Template</button>
            </div>

            {/* STATS */}
            <div className="itm-stats">
              <div className="itm-stat-card itm-stat-card--gold"   style={{ animationDelay: '0s' }}>
                <div className="itm-stat-icon">✉️</div>
                <div className="itm-stat-info"><div className="itm-stat-value">{itmStats.total}</div><div className="itm-stat-label">Total Templates</div></div>
              </div>
              <div className="itm-stat-card itm-stat-card--green"  style={{ animationDelay: '0.07s' }}>
                <div className="itm-stat-icon">✅</div>
                <div className="itm-stat-info"><div className="itm-stat-value">{itmStats.active}</div><div className="itm-stat-label">Active</div></div>
              </div>
              <div className="itm-stat-card itm-stat-card--red"    style={{ animationDelay: '0.14s' }}>
                <div className="itm-stat-icon">🚫</div>
                <div className="itm-stat-info"><div className="itm-stat-value">{itmStats.inactive}</div><div className="itm-stat-label">Inactive</div></div>
              </div>
              <div className="itm-stat-card itm-stat-card--purple" style={{ animationDelay: '0.21s' }}>
                <div className="itm-stat-icon">🎊</div>
                <div className="itm-stat-info"><div className="itm-stat-value">{itmStats.eventTypes}</div><div className="itm-stat-label">Event Types</div></div>
              </div>
            </div>

            {/* FILTERS */}
            <div className="itm-action-bar">
              <div className="itm-search-wrap">
                <span className="itm-search-icon">🔍</span>
                <input
                  type="text"
                  className="itm-search-input"
                  placeholder="Search templates by name..."
                  value={itmSearch}
                  onChange={e => { setItmSearch(e.target.value); setItmPage(1); }}
                />
              </div>
              <select className="itm-filter-select" value={itmFltEvent} onChange={e => { setItmFltEvent(e.target.value); setItmPage(1); }}>
                <option value="All">All Events</option>
                {ITM_EVENT_TYPES.map(ev => <option key={ev}>{ev}</option>)}
              </select>
              <select className="itm-filter-select" value={itmFltStatus} onChange={e => { setItmFltStatus(e.target.value); setItmPage(1); }}>
                <option value="All">All Status</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
              <div className="itm-action-bar-right">
                <button className="itm-btn-reset" onClick={itmReset}>↺ Reset</button>
              </div>
            </div>

            {/* TABLE */}
            <div className="itm-table-card">
              <div className="itm-table-header">
                <h3 className="itm-table-title">✉️ All Templates</h3>
                <span className="itm-table-count">{itmFiltered.length} templates</span>
              </div>

              <div className="itm-table-wrap">
                {itmLoading ? (
                  <div className="itm-empty">
                    <span className="itm-empty-icon">⏳</span>
                    <p className="itm-empty-text">Loading templates...</p>
                  </div>
                ) : (
                  <table className="itm-table">
                    <thead className="itm-thead">
                      <tr>
                        <th>#</th>
                        <th>Template Name</th>
                        <th>Event Type</th>
                        <th>Theme</th>
                        <th>Created</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="itm-tbody">
                      {itmPaginated.length > 0 ? itmPaginated.map((item, i) => (
                        <tr key={item.id} style={{ animationDelay: `${i * 0.05}s` }}>
                          <td><span className="itm-serial">{(itmPage - 1) * ITM_PER_PAGE + i + 1}</span></td>
                          <td>
                            <div className="itm-template-name">
                              <div className={itmThumbClass(item.theme)}>{itmThemeEmoji(item.theme)}</div>
                              <div>
                                <div className="itm-template-name-text">{item.name}</div>
                                <div className="itm-template-code">{item.orientation} • {item.fontStyle}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={itmEventBadgeClass(item.eventType)}>
                              {itmEventEmoji(item.eventType)} {item.eventType}
                            </span>
                          </td>
                          <td><span className="itm-theme-badge">{itmThemeEmoji(item.theme)} {item.theme}</span></td>
                          <td><span className="itm-date-text">📅 {item.createdAt}</span></td>
                          <td>
                            <span
                              className={`itm-status-badge itm-status-badge--${item.status.toLowerCase()}`}
                              onClick={() => itmToggleStatus(item.id)}
                              title="Click to toggle status"
                              style={{ cursor: 'pointer' }}
                            >
                              <span className="itm-status-dot" />{item.status}
                            </span>
                          </td>
                          <td>
                            <div className="itm-actions">
                              {/* View */}
                              <button
                                style={viewBtnStyle} title="View"
                                onClick={() => { setItmViewItem(item); setItmScreen('view'); window.scrollTo(0, 0); }}
                                onMouseEnter={hoverUp} onMouseLeave={hoverDown}
                              >
                                <FaEye />
                              </button>
                              {/* Edit */}
                              <button
                                style={editBtnStyle} title="Edit"
                                onClick={() => itmOpenEdit(item)}
                                onMouseEnter={hoverUp} onMouseLeave={hoverDown}
                              >
                                <FaEdit />
                              </button>
                              {/* Delete */}
                              <button
                                style={deleteBtnStyle} title="Delete"
                                onClick={() => setItmDeleteId(item.id)}
                                onMouseEnter={hoverUp} onMouseLeave={hoverDown}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7">
                            <div className="itm-empty">
                              <span className="itm-empty-icon">📭</span>
                              <p className="itm-empty-text">No templates found. Try changing filters or create a new one.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {/* PAGINATION */}
              <div className="itm-pagination">
                <span className="itm-pagination-info">
                  Showing{' '}
                  <span>{itmFiltered.length === 0 ? 0 : (itmPage - 1) * ITM_PER_PAGE + 1}</span>–
                  <span>{Math.min(itmPage * ITM_PER_PAGE, itmFiltered.length)}</span>
                  {' '}of <span>{itmFiltered.length}</span> templates
                </span>
                {itmTotalPages > 1 && (
                  <div className="itm-page-btns">
                    <button className="itm-page-btn" onClick={() => setItmPage(1)}           disabled={itmPage === 1}>«</button>
                    <button className="itm-page-btn" onClick={() => setItmPage(p => p - 1)} disabled={itmPage === 1}>‹</button>
                    {Array.from({ length: itmTotalPages }, (_, idx) => idx + 1).map(p => (
                      <button key={p} className={`itm-page-btn ${itmPage === p ? 'itm-page-btn--active' : ''}`} onClick={() => setItmPage(p)}>{p}</button>
                    ))}
                    <button className="itm-page-btn" onClick={() => setItmPage(p => p + 1)} disabled={itmPage === itmTotalPages}>›</button>
                    <button className="itm-page-btn" onClick={() => setItmPage(itmTotalPages)} disabled={itmPage === itmTotalPages}>»</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════════ CREATE / EDIT ════════ */}
        {(itmScreen === 'create' || itmScreen === 'edit') && (
          <div className="itm-form-page">
            <div className="itm-form-page-header">
              <button className="itm-btn-back" onClick={itmGoBack}>← Back</button>
              <h2 className="itm-form-page-title">{itmScreen === 'edit' ? '✏️ Edit Template' : '✉️ Create New Template'}</h2>
            </div>
            <div className="itm-form-layout">
              <div className="itm-form-card">
                <div className="itm-form-card-head"><h3 className="itm-form-card-title">📝 Template Details</h3></div>
                <div className="itm-form-body">
                  <div className="itm-form-grid">

                    {/* Template Name */}
                    <div className="itm-form-group itm-form-group--full">
                      <label className="itm-form-label">Template Name <span>*</span></label>
                      <input
                        type="text"
                        className="itm-form-input"
                        placeholder="e.g. Royal Gold Wedding"
                        value={itmForm.name}
                        onChange={e => setItmForm(f => ({ ...f, name: e.target.value }))}
                      />
                    </div>

                    {/* Event Type */}
                    <div className="itm-form-group">
                      <label className="itm-form-label">Event Type</label>
                      <select className="itm-form-select" value={itmForm.eventType} onChange={e => setItmForm(f => ({ ...f, eventType: e.target.value }))}>
                        {ITM_EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>

                    {/* Theme */}
                    <div className="itm-form-group">
                      <label className="itm-form-label">Theme Style</label>
                      <select className="itm-form-select" value={itmForm.theme} onChange={e => setItmForm(f => ({ ...f, theme: e.target.value }))}>
                        {ITM_THEMES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>

                    {/* Orientation */}
                    <div className="itm-form-group">
                      <label className="itm-form-label">Orientation</label>
                      <select className="itm-form-select" value={itmForm.orientation} onChange={e => setItmForm(f => ({ ...f, orientation: e.target.value }))}>
                        {ITM_ORIENTATIONS.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>

                    {/* Font Style */}
                    <div className="itm-form-group">
                      <label className="itm-form-label">Font Style</label>
                      <select className="itm-form-select" value={itmForm.fontStyle} onChange={e => setItmForm(f => ({ ...f, fontStyle: e.target.value }))}>
                        {ITM_FONTS.map(fn => <option key={fn}>{fn}</option>)}
                      </select>
                    </div>

                    {/* Font Color */}
                    <div className="itm-form-group itm-form-group--full">
                      <label className="itm-form-label">Font Color</label>
                      <div className="itm-color-row">
                        <input
                          type="color"
                          className="itm-color-input"
                          value={itmForm.fontColor}
                          onChange={e => setItmForm(f => ({ ...f, fontColor: e.target.value }))}
                        />
                        <input
                          type="text"
                          className="itm-form-input itm-color-hex"
                          placeholder="#92400e"
                          value={itmForm.fontColor}
                          onChange={e => setItmForm(f => ({ ...f, fontColor: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* Header Text */}
                    <div className="itm-form-group itm-form-group--full">
                      <label className="itm-form-label">Header Text <span>*</span></label>
                      <input
                        type="text"
                        className="itm-form-input"
                        placeholder="e.g. With Great Joy We Announce"
                        value={itmForm.headerText}
                        onChange={e => setItmForm(f => ({ ...f, headerText: e.target.value }))}
                      />
                    </div>

                    {/* Body Text */}
                    <div className="itm-form-group itm-form-group--full">
                      <label className="itm-form-label">Body Text</label>
                      <textarea
                        className="itm-form-textarea"
                        placeholder="You are cordially invited..."
                        value={itmForm.bodyText}
                        onChange={e => setItmForm(f => ({ ...f, bodyText: e.target.value }))}
                      />
                    </div>

                    {/* Status */}
                    <div className="itm-form-group itm-form-group--full">
                      <label className="itm-form-label">Status</label>
                      <div className="itm-status-group">
                        {['Active', 'Inactive'].map(v => (
                          <label key={v} className={`itm-status-option${v === 'Inactive' ? ' itm-status-option--inactive' : ''}`}>
                            <input
                              type="radio"
                              name="itm-status"
                              value={v}
                              checked={itmForm.status === v}
                              onChange={() => setItmForm(f => ({ ...f, status: v }))}
                            />
                            {v === 'Active' ? '✅ Active' : '🚫 Inactive'}
                          </label>
                        ))}
                      </div>
                    </div>

                  </div>

                  {/* BUTTONS */}
                  <div className="itm-form-footer">
                    <button className="itm-btn-cancel" onClick={itmGoBack} disabled={itmSaving}>Cancel</button>
                    <button className="itm-btn-save"   onClick={itmSave}   disabled={itmSaving}>
                      {itmSaving ? '⏳ Saving...' : `💾 ${itmScreen === 'edit' ? 'Update Template' : 'Save Template'}`}
                    </button>
                  </div>
                </div>
              </div>

              {/* LIVE PREVIEW */}
              <div className="itm-preview-sticky">
                <div className="itm-preview-card">
                  <div className="itm-preview-card-head">
                    <h3 className="itm-preview-card-title">Live Preview</h3>
                    <span className="itm-preview-live-dot">LIVE</span>
                  </div>
                  <div className="itm-preview-canvas"><ItmInvCard data={itmForm} /></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════ VIEW ════════ */}
        {itmScreen === 'view' && itmViewItem && (
          <div className="itm-view-page">
            <div className="itm-form-page-header">
              <button className="itm-btn-back" onClick={() => { setItmScreen('list'); window.scrollTo(0, 0); }}>← Back</button>
              <h2 className="itm-form-page-title">✉️ Template Details</h2>
            </div>
            <div className="itm-view-layout">
              <div className="itm-view-detail-card">
                <div className="itm-view-detail-head">
                  <div className="itm-view-detail-title">{itmViewItem.name}</div>
                  <div className="itm-view-detail-subtitle">Created {itmViewItem.createdAt} · {itmViewItem.theme} Theme</div>
                </div>
                <div className="itm-view-detail-body">
                  <div className="itm-view-grid">
                    <div className="itm-view-item">
                      <div className="itm-view-label">Event Type</div>
                      <div className="itm-view-value">
                        <span className={itmEventBadgeClass(itmViewItem.eventType)}>{itmEventEmoji(itmViewItem.eventType)} {itmViewItem.eventType}</span>
                      </div>
                    </div>
                    <div className="itm-view-item">
                      <div className="itm-view-label">Theme</div>
                      <div className="itm-view-value">{itmThemeEmoji(itmViewItem.theme)} {itmViewItem.theme}</div>
                    </div>
                    <div className="itm-view-item">
                      <div className="itm-view-label">Orientation</div>
                      <div className="itm-view-value">📐 {itmViewItem.orientation}</div>
                    </div>
                    <div className="itm-view-item">
                      <div className="itm-view-label">Font Style</div>
                      <div className="itm-view-value">🔤 {itmViewItem.fontStyle}</div>
                    </div>
                    <div className="itm-view-item">
                      <div className="itm-view-label">Font Color</div>
                      <div className="itm-view-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: 4, background: itmViewItem.fontColor, border: '2px solid #e7e5e4', flexShrink: 0 }} />
                        {itmViewItem.fontColor}
                      </div>
                    </div>
                    <div className="itm-view-item">
                      <div className="itm-view-label">Status</div>
                      <div className="itm-view-value">
                        <span className={`itm-status-badge itm-status-badge--${itmViewItem.status.toLowerCase()}`}>
                          <span className="itm-status-dot" />{itmViewItem.status}
                        </span>
                      </div>
                    </div>
                    <div className="itm-view-item itm-view-item--full">
                      <div className="itm-view-label">Header Text</div>
                      <div className="itm-view-value">{itmViewItem.headerText}</div>
                    </div>
                    <div className="itm-view-item itm-view-item--full">
                      <div className="itm-view-label">Body Text</div>
                      <div className="itm-view-value" style={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>{itmViewItem.bodyText}</div>
                    </div>
                    <div className="itm-view-item itm-view-item--full">
                      <div className="itm-view-label">Footer Text</div>
                      <div className="itm-view-value">{itmViewItem.footerText || '—'}</div>
                    </div>
                  </div>
                  <div className="itm-view-actions">
                    <button className="itm-btn-preview-lg" onClick={() => { setItmPrevData(itmViewItem); setItmShowPrev(true); }}>🔍 Preview Design</button>
                    <button className="itm-btn-edit-lg"    onClick={() => itmOpenEdit(itmViewItem)}>✏️ Edit Template</button>
                    <button className="itm-btn-delete-lg"  onClick={() => setItmDeleteId(itmViewItem.id)}>🗑️ Delete</button>
                  </div>
                </div>
              </div>

              {/* VIEW PREVIEW */}
              <div className="itm-preview-sticky">
                <div className="itm-preview-card">
                  <div className="itm-preview-card-head">
                    <h3 className="itm-preview-card-title">Design Preview</h3>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309' }}>{itmViewItem.theme} Theme</span>
                  </div>
                  <div className="itm-preview-canvas"><ItmInvCard data={itmViewItem} /></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════ DELETE MODAL ════════ */}
        {itmDeleteId && (
          <div className="itm-overlay" onClick={() => setItmDeleteId(null)}>
            <div className="itm-modal itm-delete-modal" onClick={e => e.stopPropagation()}>
              <div className="itm-modal-head itm-modal-head--red">
                <p className="itm-modal-head-label">Confirm Action</p>
                <h2 className="itm-modal-head-title">🗑️ Delete Template</h2>
                <button className="itm-modal-close" onClick={() => setItmDeleteId(null)}>✕</button>
              </div>
              <div className="itm-delete-body">
                <span className="itm-delete-icon">🗑️</span>
                <h3 className="itm-delete-title">Are you sure?</h3>
                {itmDeleteTarget && <div className="itm-delete-name">"{itmDeleteTarget.name}"</div>}
                <p className="itm-delete-text">This template will be <strong>permanently deleted.</strong><br />This action cannot be undone!</p>
                <div className="itm-delete-btns">
                  <button className="itm-btn-no"          onClick={() => setItmDeleteId(null)}>No, Cancel</button>
                  <button className="itm-btn-confirm-del" onClick={itmConfirmDelete}>🗑️ Yes, Delete</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════ PREVIEW MODAL ════════ */}
        {itmShowPrev && itmPrevData && (
          <div className="itm-overlay" onClick={() => setItmShowPrev(false)}>
            <div className="itm-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
              <div className="itm-modal-head">
                <p className="itm-modal-head-label">Full Preview</p>
                <h2 className="itm-modal-head-title">✉️ {itmPrevData.name}</h2>
                <button className="itm-modal-close" onClick={() => setItmShowPrev(false)}>✕</button>
              </div>
              <div className="itm-preview-modal-body">
                <div className="itm-preview-modal-card"><ItmInvCard data={itmPrevData} /></div>
              </div>
            </div>
          </div>
        )}

        {/* TOAST */}
        {itmToast && <ItmToast msg={itmToast.msg} type={itmToast.type} />}

      </div>
    </AdminLayout>
  );
};

export default InvitationsTemplate;